import React, { useCallback, useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import Icon from '../../common/Icons';
import { AdminPersonnelPageHeader } from '../ui';
import { useApToast } from '../hooks/useApToast';
import CompanyFormModal from '../companies/CompanyFormModal';
import CompanyDetailsModal from '../companies/CompanyDetailsModal';
import CompanyListTable from '../../companies/CompanyListTable';
import CompanyService from '../../../services/companyService';
import useSmieAffiliations from '../../../hooks/useSmieAffiliations';
import useCompanyCities from '../../../hooks/useCompanyCities';
import useCompanyListFilters from '../../../hooks/useCompanyListFilters';
import CompanyListStats from '../../companies/CompanyListStats';
import CompanyListFilters from '../../companies/CompanyListFilters';
import {
  buildCompanyPayload,
  buildEmptyCompanyForm,
  companyFormFromRecord
} from '../../companies/companyListUtils';
import { AP_COPY } from '../adminPersonnelCopy';
import '../AdminPersonnelPortal.css';

const STATUS_FILTERS_AP = [
  { id: 'all', label: 'Toutes' },
  { id: 'active', label: AP_COPY.COMPANIES.STATUS_ACTIVE },
  { id: 'inactive', label: AP_COPY.COMPANIES.STATUS_INACTIVE }
];

export default function AdminPersonnelCompaniesPage() {
  const { showToast } = useApToast();
  const {
    affiliations,
    loading: affiliationsLoading,
    defaultAffiliation,
    resolveForCompany,
    reload: reloadAffiliations,
    error: affiliationsError
  } = useSmieAffiliations();

  const {
    cities,
    loading: citiesLoading,
    defaultCity,
    reload: reloadCities,
    error: citiesError
  } = useCompanyCities();

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [formData, setFormData] = useState(() => buildEmptyCompanyForm());
  const [actionMenuCompany, setActionMenuCompany] = useState(null);
  const [actionMenuPos, setActionMenuPos] = useState({ top: 0, left: 0 });

  const loadCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const response = await CompanyService.getAllCompanies();
      setCompanies(response.data || []);
    } catch (err) {
      console.error('Erreur chargement sociétés:', err);
      showToast(err.message || 'Impossible de charger les sociétés', 'danger');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const {
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    affiliationFilter,
    setAffiliationFilter,
    cityFilter,
    setCityFilter,
    filteredCompanies,
    stats,
    hasActiveFilters,
    clearFilters
  } = useCompanyListFilters(companies, { affiliations, cities });

  const openActionMenu = (event, company) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 240;
    const left = Math.min(rect.left, window.innerWidth - menuWidth - 16);
    setActionMenuPos({ top: rect.bottom + 8, left: Math.max(16, left) });
    setActionMenuCompany(company);
  };

  const handleCreate = () => {
    setEditingCompany(null);
    setFormData(buildEmptyCompanyForm(defaultAffiliation, defaultCity));
    setShowModal(true);
  };

  const handleEdit = (company) => {
    setEditingCompany(company);
    setFormData(companyFormFromRecord(company, affiliations, defaultAffiliation, cities, defaultCity));
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const nextValue =
      name === 'company_code' ? value.toUpperCase().replace(/[^A-Z0-9_-]/g, '') : value;
    setFormData((prev) => ({ ...prev, [name]: nextValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.company_code || formData.company_code.trim().length < 2) {
      showToast('Le code société est obligatoire (2 caractères min.)', 'danger');
      return;
    }
    if (!formData.name || formData.name.trim().length < 2) {
      showToast('Le nom doit contenir au moins 2 caractères', 'danger');
      return;
    }
    if (!formData.affiliation_id) {
      showToast('Sélectionnez une affiliation SMIE', 'danger');
      return;
    }
    const payload = buildCompanyPayload(formData);
    try {
      if (editingCompany) {
        await CompanyService.updateCompany(editingCompany.id, payload);
        showToast('Société mise à jour', 'success');
      } else {
        await CompanyService.createCompany(payload);
        showToast('Société créée', 'success');
      }
      setShowModal(false);
      await loadCompanies();
    } catch (err) {
      showToast(err.message || 'Erreur lors de la sauvegarde', 'danger');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer définitivement cette société ?')) return;
    try {
      await CompanyService.deleteCompany(id);
      showToast('Société supprimée', 'success');
      setActionMenuCompany(null);
      await loadCompanies();
    } catch (err) {
      showToast(err.message || 'Erreur lors de la suppression', 'danger');
    }
  };

  const handleToggleStatus = async (company) => {
    try {
      const newStatus = !company.is_active;
      if (newStatus) {
        await CompanyService.updateCompany(company.id, { is_active: true });
      } else {
        await CompanyService.deactivateCompany(company.id);
      }
      showToast(`Société ${newStatus ? 'activée' : 'désactivée'}`, 'success');
      setActionMenuCompany(null);
      await loadCompanies();
    } catch (err) {
      showToast(err.message || 'Erreur changement de statut', 'danger');
    }
  };

  const hasCompanies = filteredCompanies.length > 0;
  const C = AP_COPY.COMPANIES;

  return (
    <>
      <div className="ap-dash ap-companies-page">
        <AdminPersonnelPageHeader
          badge={AP_COPY.DASHBOARD.BADGE}
          title={C.PAGE_TITLE}
          description={C.PAGE_SUBTITLE}
          actions={
            <button type="button" className="ap-co-create-btn" onClick={handleCreate}>
              <Icon name="plus" size={14} />
              <span>{C.BTN_CREATE}</span>
            </button>
          }
        />

        <section className="ap-co-shell ap-panel">
          <CompanyListStats stats={stats} id="ap-co-list-stats" />

          <CompanyListFilters
            search={search}
            onSearchChange={setSearch}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            statusLabels={STATUS_FILTERS_AP}
            affiliationFilter={affiliationFilter}
            onAffiliationFilterChange={setAffiliationFilter}
            affiliationOptions={affiliations}
            affiliationAllLabel={C.AFFILIATION_ALL}
            cityFilter={cityFilter}
            onCityFilterChange={setCityFilter}
            cities={cities}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={clearFilters}
            onRefresh={loadCompanies}
            loading={loading}
            filteredCount={filteredCompanies.length}
            repositoryTotal={companies.length}
            searchPlaceholder={C.SEARCH_PLACEHOLDER}
          />

          {loading ? (
            <div className="ap-co-state">
              <Spinner animation="border" variant="primary" />
              <p>Chargement…</p>
            </div>
          ) : companies.length === 0 ? (
            <div className="ap-co-state ap-co-state--empty">
              <div className="ap-co-state__icon">
                <Icon name="building" size={36} />
              </div>
              <h3>Aucune société</h3>
              <button type="button" className="ap-co-create-btn" onClick={handleCreate}>
                <Icon name="plus" size={14} />
                <span>{C.BTN_CREATE}</span>
              </button>
            </div>
          ) : !hasCompanies ? (
            <div className="ap-co-state">
              <Icon name="inbox" size={40} className="mb-2 opacity-50" />
              <h6>Aucun résultat</h6>
              <p>Affinez la recherche ou réinitialisez les filtres.</p>
              {hasActiveFilters ? (
                <button type="button" className="co-list-clear-btn mt-2" onClick={clearFilters}>
                  Réinitialiser les filtres
                </button>
              ) : null}
            </div>
          ) : (
            <div key={`${search}-${statusFilter}-${affiliationFilter}-${cityFilter}`}>
              <CompanyListTable
                companies={filteredCompanies}
                resolveForCompany={resolveForCompany}
                cities={cities}
                onOpenActionMenu={openActionMenu}
              />
            </div>
          )}
        </section>

        {actionMenuCompany && (
          <>
            <button
              type="button"
              className="ap-co-action-backdrop"
              onClick={() => setActionMenuCompany(null)}
            />
            <div
              className="ap-co-action-floating"
              style={{ top: actionMenuPos.top, left: actionMenuPos.left }}
            >
              <button
                type="button"
                className="ap-co-action-item"
                onClick={() => {
                  setActionMenuCompany(null);
                  setSelectedCompany(actionMenuCompany);
                  setShowDetailModal(true);
                }}
              >
                <Icon name="eye" size={14} />
                <span>Voir détail</span>
              </button>
              <button
                type="button"
                className="ap-co-action-item"
                onClick={() => {
                  setActionMenuCompany(null);
                  handleEdit(actionMenuCompany);
                }}
              >
                <Icon name="edit" size={14} />
                <span>Modifier</span>
              </button>
              <button
                type="button"
                className="ap-co-action-item"
                onClick={() => handleToggleStatus(actionMenuCompany)}
              >
                <Icon
                  name={actionMenuCompany.is_active ? 'eyeSlash' : 'checkCircle'}
                  size={14}
                />
                <span>{actionMenuCompany.is_active ? 'Désactiver' : 'Réactiver'}</span>
              </button>
              <div className="ap-co-action-separator" />
              <button
                type="button"
                className="ap-co-action-item ap-co-action-danger"
                onClick={() => handleDelete(actionMenuCompany.id)}
              >
                <Icon name="trash" size={14} />
                <span>Supprimer</span>
              </button>
            </div>
          </>
        )}

        <CompanyFormModal
          show={showModal}
          onHide={() => setShowModal(false)}
          formData={formData}
          onChange={handleInputChange}
          onSubmit={handleSubmit}
          editingCompany={editingCompany}
          affiliations={affiliations}
          defaultAffiliation={defaultAffiliation}
          affiliationsLoading={affiliationsLoading}
          onReloadAffiliations={reloadAffiliations}
          affiliationsError={affiliationsError}
          cities={cities}
          defaultCity={defaultCity}
          citiesLoading={citiesLoading}
          onReloadCities={reloadCities}
          citiesError={citiesError}
        />

        <CompanyDetailsModal
          show={showDetailModal}
          onHide={() => setShowDetailModal(false)}
          company={selectedCompany}
          affiliationRef={selectedCompany ? resolveForCompany(selectedCompany) : null}
          onEdit={handleEdit}
        />
      </div>
    </>
  );
}
